import { Badge } from '@chakra-ui/react'
import { useCachedInsurer } from '@hb/react'
import {
  BaseInsuranceCoverage,
  getDateString,
  InsuranceCoverage,
  PopulatedAssessment,
} from '@hb/shared'
import { useMemo } from 'react'

export interface TerminationStatus {
  text: string
  status: 'terminated' | 'pending'
}
export const useTerminationStatus = (
  coverage: InsuranceCoverage | BaseInsuranceCoverage | null | undefined,
): TerminationStatus | null => {
  const { terminationDate } = coverage ?? {}
  return useMemo(() => {
    if (!terminationDate) return null
    const now = Date.now()
    if (terminationDate < now) {
      return {
        text: `Terminated on ${getDateString(terminationDate, 'short')}`,
        status: 'terminated',
      }
    }
    const duration = terminationDate - now
    if (duration > 9 * 7 * 24 * 60 * 60 * 1000) {
      return {
        text: `Terminates on ${getDateString(terminationDate, 'short')}`,
        status: 'terminated',
      }
    }
    return {
      text: `Terminates in ${Math.floor(duration / (1000 * 60 * 60 * 24))} days`,
      status: 'pending',
    }
  }, [terminationDate])
}

export const CoverageTerminationStatus = ({
  coverage,
  terminationStatus,
  assessment,
  wrap,
  simple,
}: {
  coverage: InsuranceCoverage | BaseInsuranceCoverage | null | undefined
  terminationStatus: TerminationStatus | null | undefined
  assessment: PopulatedAssessment | null
  wrap?: boolean
  simple?: boolean
}) => {
  const terminatesBeforeDelivery = useMemo(() => {
    if (!assessment) return false
    const { mergedData } = assessment
    const { edd } = mergedData?.delivery ?? {}
    const { terminationDate } = coverage ?? {}
    if (!terminationDate || !edd) return false
    return terminationDate < edd
  }, [assessment, coverage])
  const { data: insurer } = useCachedInsurer(coverage?.insuranceProviderId ?? null)
  return terminationStatus ? (
    <Badge
      boxShadow={terminatesBeforeDelivery ? '1px 1px 10px #ee4444' : '1px 1px 3px #00000033'}
      maxW={wrap ? '185px' : 'none'}
      px={2}
      textAlign="center"
      whiteSpace={wrap ? 'wrap' : 'nowrap'}
      colorScheme={terminationStatus.status === 'terminated' ? 'red' : 'orange'}>
      {simple
        ? terminationStatus.text
        : `${insurer?.name || 'Insurance'} plan ${terminationStatus.text}`}
    </Badge>
  ) : null
}
